import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Typography, Button, Alert, CircularProgress, Paper } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

// API URL for the Java backend - without the /api suffix since it's included in the endpoints
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Simple JSON prettifier component
const JsonView = ({ src }: { src: any }) => {
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: '#272822',
        color: '#f8f8f2',
        borderRadius: 1,
        overflow: 'auto',
        maxHeight: '400px',
        fontFamily: 'monospace'
      }}
    >
      <pre>{JSON.stringify(src, null, 2)}</pre>
    </Box>
  );
};

const AdminKrakenPage: React.FC = () => {
  const { state } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [rawData, setRawData] = useState<any>(null);
  const [healthStatus, setHealthStatus] = useState<any>(null);
  const [apiKeys, setApiKeys] = useState<any>(null);

  // Function to fetch raw Kraken data
  const fetchRawKrakenData = async () => {
    setLoading(true);
    setError(null);
    setRawData(null);
    
    try {
      if (!state.user || !state.user.email) {
        setError('You must be logged in to fetch raw Kraken data');
        setLoading(false);
        return;
      }
      
      console.log('Fetching completely unmodified raw Kraken data from Java API...');
      console.log('API URL:', `/api/kraken/raw-account-data`); // Using relative path for proxy
      console.log('User email:', state.user.email);
      
      const response = await axios.get(`/api/kraken/raw-account-data`, {
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': state.user.email
        },
        params: {
          email: state.user.email // Fallback in case headers are not processed correctly
        },
        timeout: 60000 // 60 second timeout
      });
      
      console.log('Raw Kraken data response:', response.data);
      setRawData(response.data);
    } catch (error: any) {
      console.error('Error fetching raw Kraken data:', error);
      
      let errorMessage = 'Failed to fetch raw Kraken data';
      
      if (error.response) {
        console.error('Error response:', error.response);
        if (error.response.data && error.response.data.message) {
          errorMessage = `${errorMessage}: ${error.response.data.message}`;
        }
      } else if (error.request) {
        errorMessage = `${errorMessage}: No response received (timeout or network issue)`;
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Function to check Kraken API health
  const checkKrakenApiHealth = async () => {
    setLoading(true);
    setError(null);
    setHealthStatus(null);
    
    try {
      if (!state.user || !state.user.email) {
        setError('You must be logged in to check Kraken API health');
        setLoading(false);
        return;
      }
      
      console.log('Checking Kraken API health...');
      
      const response = await axios.get(`/api/kraken/health`, {
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': state.user.email
        },
        params: {
          email: state.user.email
        },
        timeout: 10000 // 10 second timeout
      });
      
      console.log('Health check response:', response.data);
      setHealthStatus(response.data);
    } catch (error: any) {
      console.error('Error checking Kraken API health:', error);
      
      let errorMessage = 'Failed to check Kraken API health';
      
      if (error.response) {
        console.error('Error response:', error.response);
        if (error.response.data && error.response.data.message) {
          errorMessage = `${errorMessage}: ${error.response.data.message}`;
        }
      } else if (error.request) {
        errorMessage = `${errorMessage}: No response received (timeout or network issue)`;
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Function to get Kraken API keys
  const getKrakenApiKeys = async () => {
    setLoading(true);
    setError(null);
    setApiKeys(null);
    
    try {
      if (!state.user || !state.user.email) {
        setError('You must be logged in to get Kraken API keys');
        setLoading(false);
        return;
      }
      
      console.log('Getting Kraken API keys...');
      
      const response = await axios.get(`${API_URL}/api/kraken/api-keys`, {
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': state.user.email
        },
        params: {
          email: state.user.email
        },
        timeout: 10000 // 10 second timeout
      });
      
      console.log('API keys response:', response.data);
      
      // Add status field to the API keys response
      const enhancedResponse = {
        ...response.data,
        status: 'success'
      };
      
      console.log('Enhanced API keys response:', enhancedResponse);
      setApiKeys(enhancedResponse);
    } catch (error: any) {
      console.error('Error getting Kraken API keys:', error);
      
      let errorMessage = 'Failed to get Kraken API keys';
      
      if (error.response) {
        console.error('Error response:', error.response);
        if (error.response.data && error.response.data.message) {
          errorMessage = `${errorMessage}: ${error.response.data.message}`;
        }
      } else if (error.request) {
        errorMessage = `${errorMessage}: No response received (timeout or network issue)`;
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!state.user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Admin Kraken Page</Typography>
        <Alert severity="error">You must be logged in to view this page</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Kraken Admin Dashboard</Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={fetchRawKrakenData} 
          disabled={loading}
        >
          Get Raw Kraken Data
        </Button>
        <Button 
          variant="contained" 
          color="secondary" 
          onClick={checkKrakenApiHealth} 
          disabled={loading}
        >
          Check API Health
        </Button>
        <Button 
          variant="contained" 
          color="info" 
          onClick={getKrakenApiKeys} 
          disabled={loading}
        >
          Get API Keys
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        This page displays completely unmodified raw data from the Kraken API for debugging purposes.
      </Alert>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {rawData && (
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Raw Kraken Account Data</Typography>
          <JsonView src={rawData} />
        </Paper>
      )}
      
      {healthStatus && (
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Kraken API Health Status</Typography>
          <JsonView src={healthStatus} />
        </Paper>
      )}
      
      {apiKeys && (
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Kraken API Keys</Typography>
          <JsonView src={apiKeys} />
        </Paper>
      )}
    </Box>
  );
};

export default AdminKrakenPage;
